import { useState, useMemo } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ProfileBadge from '../common/ProfileBadge';
import ProfileMenu from '../common/ProfileMenu';
import { useLanguage } from '../../contexts/LanguageContext';

interface NavItem {
  name: string;
  href: string;
}

export default function AdminLayout() {
  const { t } = useLanguage();
  const location = useLocation();
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);

  // Flat list for page title lookup (navigation is now in ProfileMenu)
  const allNavItems: NavItem[] = useMemo(() => [
    { name: 'Operations Center', href: '/admin/ops' },
    { name: 'Seed Browsers', href: '/admin/seeds' },
    { name: 'Store Browser', href: '/admin/store-browser' },
    { name: 'Error Investigation', href: '/admin/errors' },
    { name: 'Dashboard', href: '/admin' },
    { name: 'Reports', href: '/admin/reports' },
    { name: 'User Insights', href: '/admin/user-insights' },
    { name: 'System', href: '/admin/system' },
    { name: 'Users', href: '/admin/users' },
    { name: 'Bug Reports', href: '/admin/bug-reports' },
    { name: 'Website', href: '/admin/website' },
    { name: 'Translations', href: '/admin/translations' },
    { name: 'AI Settings', href: '/admin/ai-settings' },
    { name: 'SMCC', href: '/admin/smcc' },
    { name: 'Dev Builds', href: '/admin/dev-builds' },
    { name: 'Settings', href: '/admin/settings' },
  ], []);

  // Get current page title
  const currentPage = useMemo(() => {
    return allNavItems.find((item) => {
      if (item.href === '/admin') {
        return location.pathname === '/admin';
      }
      return location.pathname.startsWith(item.href);
    });
  }, [location.pathname, allNavItems]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Header */}
      <header className="bg-gray-900 text-white">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center">
                <img src="/logo-light.svg" alt="RUMIO" className="h-7" />
              </Link>
              <span className="px-2 py-0.5 text-xs font-medium bg-amber-500 text-amber-950 rounded">
                {t('adminLayout.admin', 'Admin')}
              </span>
            </div>

            <div className="flex items-center gap-4 md:gap-6">
              <Link
                to="/account"
                className="hidden md:flex items-center gap-2 text-sm text-gray-300 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('adminLayout.backToAccount', 'Back to Account')}
              </Link>
              <ProfileBadge onClick={() => setAdminMenuOpen(true)} />
            </div>
          </div>
        </div>
      </header>

      {/* Profile Menu (unified with admin section) */}
      <ProfileMenu
        isOpen={adminMenuOpen}
        onClose={() => setAdminMenuOpen(false)}
      />

      {/* Main content */}
      <main className="p-6">
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {currentPage?.name || 'Admin'}
          </h1>
        </div>

        {/* Page content */}
        <Outlet />
      </main>
    </div>
  );
}
